import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { GestureResponderEvent, Platform, Pressable, View } from 'react-native';
import {
  DeviceTheme,
  GetTransactionListFilter,
  GetTransactionListTransactionDto,
  GetTransactionListTransactionType,
  TransactionsClient,
} from '../../../api/api.g';
import useApi from '../../../hooks/api-hook';
import { useAppSelector } from '../../../hooks/store-hook';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import NavigationElements from '../../../models/NavigationElements';
import Label, { LabelType } from '../../common/Label';
import MyAccountPageWrapper from '../MyAccountPageWrapper';
import MyTransactionsItem from './MyTransactionsItem';
import { faSliders, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import CustomCheckbox from '../../common/CustomCheckbox';
import useStyles from '../../../hooks/styles-hook';
import CustomButton from '../../common/CustomButton';
import Loading from '../../common/Loading';

const filterItems = [
  { key: 1, value: 'transaction-type.adjustment' },
  { key: 2, value: 'transaction-type.validation' },
  { key: 3, value: 'transaction-type.mission' },
  { key: 4, value: 'transaction-type.cashout' },
  { key: 5, value: 'transaction-type.mission-referral' },
];

const MyTransactions = () => {
  const theme = useCustomTheme();
  const t = useTranslation();
  const authStore = useAppSelector((selector) => selector.auth);
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const apiTransactions = useApi(TransactionsClient);
  const isDarkMode = userPrefs.theme === DeviceTheme.Dark;
  const pageSize = 20;

  const styles = useStyles((section) => section.myMissions);

  const [transactionsItems, setTransactionsItems] = useState<GetTransactionListTransactionDto[]>([]);
  const [selectedFilterItems, setSelectedFilterItems] = useState<number[]>([]);
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [continuationToken, setContinuationToken] = useState<string>();
  const isAllSelected = selectedFilterItems.length === filterItems.length;

  const transactionsQuery = useQuery({
    enabled: !!authStore.idToken,
    queryKey: ['transactions', authStore.logged, selectedFilterItems, continuationToken],
    queryFn: ({ signal }) => {
      const filterValues = new GetTransactionListFilter();

      if (!isAllSelected && selectedFilterItems.length > 0) {
        filterValues.transactionTypes = selectedFilterItems.map<GetTransactionListTransactionType>((filterItem) => filterItem as GetTransactionListTransactionType);
      } else {
        filterValues.transactionTypes = undefined;
      }

      const promise = apiTransactions.getTransactions(continuationToken, pageSize, filterValues, apiTransactions.tokenSource?.token);

      // Cancel the request if React Query signals to abort.
      signal?.addEventListener('abort', () => {
        apiTransactions.tokenSource?.cancel('Query was cancelled by React Query');
        apiTransactions.onCancel();
      });

      return promise;
    },
  });

  useEffect(() => {
    const fetchedTransactions = transactionsQuery.data?.transactions?.items;

    if (!transactionsQuery.isSuccess || !fetchedTransactions?.length) {
      return;
    }

    setTransactionsItems((prev) => (continuationToken ? [...prev, ...fetchedTransactions] : [...fetchedTransactions]));
  }, [continuationToken, transactionsQuery.data, transactionsQuery.isSuccess]);

  const displayedTransactions = transactionsItems.length > 0 ? transactionsItems : (transactionsQuery.data?.transactions?.items ?? []);
  const isTransactionsLoading = (transactionsQuery.isLoading || transactionsQuery.isFetching) && displayedTransactions.length === 0;
  const transactionsListKey =
    displayedTransactions.length > 0
      ? `${displayedTransactions[0]?.id ?? 'first'}_${displayedTransactions[displayedTransactions.length - 1]?.id ?? 'last'}_${displayedTransactions.length}`
      : 'empty';

  const handleFilterClick = (checked: boolean, transactionType: number) => {
    if (checked) {
      if (selectedFilterItems.includes(transactionType)) {
        return;
      }

      resetList();
      setSelectedFilterItems((prev) => [...prev, transactionType]);
      return;
    }

    if (!selectedFilterItems.includes(transactionType)) {
      return;
    }

    resetList();
    setSelectedFilterItems((prev) => prev.filter((item) => item !== transactionType));
  };

  const resetList = () => {
    setTransactionsItems([]);
    setContinuationToken(undefined);
  };

  const handleFilterButtonPress = () => {
    setShowTagPopup((prev) => !prev);
  };

  const handleSelectAll = (e: GestureResponderEvent) => {
    e.preventDefault();

    if (isAllSelected) {
      setSelectedFilterItems([]);
    } else {
      setSelectedFilterItems(filterItems.map((filterItem) => filterItem.key));
    }

    resetList();
  };

  return (
    <MyAccountPageWrapper
      navigationTitle={t('my_profile')}
      title={t('my_transactions')}
      navigationAction={NavigationElements.menu}
      scrollOnChange={continuationToken !== undefined}
      moreInfoComponent={{
        showTitle: true,
        externalLink: t('lanla_website_support'),
        title: t('help_support'),
        children: `## Rursus septem

**Freta sanguine** prece non tibi circumfuso socios. Ubi quodsi longo quid
repugnat esse isto modo sensisse ecquis. Securi cremet. Ego poscat omnis, sui
tene faciat in lacrimis sibi. Flectitur pennis tendentes exstitit ordine.`,
      }}
    >
      <View style={{ flexGrow: 1 }}>
        {showTagPopup && (
          <Pressable
            style={{
              backgroundColor: 'transparent',
              position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1000,
            }}
            onPress={() => setShowTagPopup(false)}
          />
        )}
        <View style={styles.newVersion.list.filterTransactions}>
          <Pressable onPress={handleFilterButtonPress} style={{ flexDirection: 'row', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faSliders} style={styles.newVersion.list.filterIcon} color={(isDarkMode ? 'white' : theme.text.primary) as any} />
            <Label variant={LabelType.title}>{t('filter-by-transaction-type')}</Label>
          </Pressable>
          <View style={[styles.newVersion.list.popupTagFilterBase, !showTagPopup ? styles.newVersion.list.popupTag.hide : styles.newVersion.list.popupTag]}>
            <Pressable id={'selectAll_button'} style={styles.newVersion.list.popupTagFilter.selectAll} onPress={handleSelectAll}>
              <Label variant={LabelType.titleDarkSmall}>{isAllSelected ? t('deselect_all') : t('select_all')}</Label>
            </Pressable>
            <>
              {filterItems.map((item) => {
                return (
                  <CustomCheckbox
                    small
                    id={`tag_${item.key}`}
                    key={`tag_${item.key}`}
                    checked={selectedFilterItems.findIndex((selectedItem) => selectedItem === item.key) > -1}
                    value={item.key}
                    label={t(item.value)}
                    style={styles.newVersion.list.popupTag.item}
                    onChange={(checked) => {
                      handleFilterClick(checked, item.key);
                    }}
                  />
                );
              })}
            </>
          </View>
        </View>
        <View>
          {displayedTransactions.length > 0 ? (
            <MyTransactionsItem key={transactionsListKey} transactions={displayedTransactions} />
          ) : isTransactionsLoading ? (
            <Loading style={{ position: 'relative', height: 120, backgroundColor: 'transparent' }} />
          ) : (
            <View style={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: 16 }}>
              <FontAwesomeIcon icon={faWarning} size={32} color={theme.text.titleLight.toString()} />
              <Label style={{ marginTop: 6 }} variant={LabelType.titlePrimarySmall}>
                {t('no-transactions')}
              </Label>
            </View>
          )}
          {transactionsQuery.data?.transactions?.continuationToken && (
            <CustomButton
              title={t('load-more')}
              style={{ marginBottom: 12, marginTop: 12, height: 40, width: '80%', maxWidth: 200, alignSelf: 'center' }}
              secondary
              onPress={() => setContinuationToken(transactionsQuery.data?.transactions?.continuationToken)}
            />
          )}
        </View>
      </View>
    </MyAccountPageWrapper>
  );
};

export default MyTransactions;
